import { currentLocale, formatDate } from 'i18n/format';
import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, MenuItem, Popover, Select, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTokens } from 'theme';

const MONTHS = () => ([translate('assistant.dashboardDateFieldJanuary'), translate('assistant.dashboardDateFieldFebruary'), translate('assistant.dashboardDateFieldMarch'), translate('assistant.dashboardDateFieldApril'), translate('assistant.dashboardDateFieldMay'), translate('assistant.dashboardDateFieldJune'), translate('assistant.dashboardDateFieldJuly'), translate('assistant.dashboardDateFieldAugust'), translate('assistant.dashboardDateFieldSeptember'), translate('assistant.dashboardDateFieldOctober'), translate('assistant.dashboardDateFieldNovember'), translate('assistant.dashboardDateFieldDecember')]);
const WEEKDAYS = () => ([translate('assistant.dashboardDateFieldMonday'), translate('assistant.dashboardDateFieldTuesday'), translate('assistant.dashboardDateFieldWednesday'), translate('assistant.dashboardDateFieldThursday'), translate('assistant.dashboardDateFieldFriday'), translate('assistant.dashboardDateFieldSaturday'), translate('assistant.dashboardDateFieldSunday')]);
const SHORT_WEEKDAYS = () => ([translate('assistant.dashboardDateFieldMon'), translate('assistant.dashboardDateFieldTue'), translate('assistant.dashboardDateFieldWed'), translate('assistant.dashboardDateFieldThu'), translate('assistant.dashboardDateFieldFri'), translate('assistant.dashboardDateFieldSat'), translate('assistant.dashboardDateFieldSun')]);
const DAY = 86400000;
const pad = (value) => String(value).padStart(2, '0');
const iso = (date) => `${String(date.getUTCFullYear()).padStart(4, '0')}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
const dateAt = (year, month, day) => {
  const date = new Date(0);
  date.setUTCFullYear(year, month, day);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};
const fromISO = (value) => dateAt(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
const pretty = (value) => currentLocale() === 'ru-RU' ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}` : `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`;
const todayISO = () => {
  const now = new Date();
  return iso(dateAt(now.getFullYear(), now.getMonth(), now.getDate()));
};
const labelDate = (value) => formatDate(fromISO(value), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const moveMonth = (value, offset) => {
  const date = fromISO(value);
  const month = dateAt(date.getUTCFullYear(), date.getUTCMonth() + offset, 1);
  const last = dateAt(month.getUTCFullYear(), month.getUTCMonth() + 1, 0).getUTCDate();
  return iso(dateAt(month.getUTCFullYear(), month.getUTCMonth(), Math.min(date.getUTCDate(), last)));
};

/** Reveal focus inside a cropped calendar without scrolling the outer chat. */
const focusCalendarDay = (grid, paper, value) => {
  const target = grid?.querySelector(`[data-date="${value}"]`);
  if (!target) return;
  target.focus({ preventScroll: true });
  if (!paper) return;
  const height = paper.clientHeight;
  if (paper.scrollHeight <= height) return;
  const top = paper.getBoundingClientRect().top + paper.clientTop;
  const bottom = top + height;
  const bounds = target.getBoundingClientRect();
  if (bounds.top < top) paper.scrollTop -= top - bounds.top;
  else if (bounds.bottom > bottom) paper.scrollTop += bounds.bottom - bottom;
};

/** Exact calendar dates only; accept Russian keyboard input and existing ISO values. */
export function parseDashboardDate(value) {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  const canonical = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const local = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  const english = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (!canonical && !local && !english) return undefined;
  const [year, month, day] = canonical ? canonical.slice(1).map(Number) : english ? [Number(english[3]), Number(english[1]), Number(english[2])] : [Number(local[3]), Number(local[2]), Number(local[1])];
  if (year < 1 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const date = dateAt(year, month - 1, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? iso(date) : undefined;
}

export const isDashboardDateISO = (value) => typeof value === 'string' && parseDashboardDate(value) === value;

/** A themed, keyboard-accessible calendar. Invalid drafts remain available to the form validator. */
export default function DashboardDateField({ label, valueISO = '', onChangeISO, min, max }) {
  const { t: translate, i18n } = useTranslation();
  const t = useTokens();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const inputRef = useRef(null);
  const gridRef = useRef(null);
  const paperRef = useRef(null);
  const emitted = useRef(null);
  const returnFocus = useRef(false);
  const titleId = useId();
  const [text, setText] = useState(() => isDashboardDateISO(valueISO) ? pretty(valueISO) : valueISO);
  const [blurred, setBlurred] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [cursor, setCursor] = useState(todayISO);
  const [yearText, setYearText] = useState(() => todayISO().slice(0, 4));
  const [focusDay, setFocusDay] = useState(false);
  const lower = isDashboardDateISO(min) ? min : '0001-01-01';
  const upper = isDashboardDateISO(max) ? max : '9999-12-31';
  const validBounds = lower <= upper;
  const clamp = (value) => value < lower ? lower : value > upper ? upper : value;
  const inRange = (value) => validBounds && value >= lower && value <= upper;
  const parsed = parseDashboardDate(text);
  const invalid = Boolean(text.trim()) && (!parsed || !inRange(parsed));
  const selected = parseDashboardDate(valueISO);
  const today = todayISO();
  const month = fromISO(cursor).getUTCMonth();
  const year = fromISO(cursor).getUTCFullYear();
  const open = Boolean(anchor);

  useEffect(() => {
    if (valueISO === emitted.current) return;
    setText(isDashboardDateISO(valueISO) ? pretty(valueISO) : valueISO);
    setBlurred(false);
  }, [valueISO]);
  // Reformat committed dates when switching language without altering their ISO value.
  useEffect(() => {
    setText((current) => { const date = parseDashboardDate(current); return date ? pretty(date) : current; });
  }, [i18n.resolvedLanguage]);
  useEffect(() => setYearText(String(year)), [year]);
  useEffect(() => {
    if (!open || !focusDay) return;
    focusCalendarDay(gridRef.current, paperRef.current, cursor);
    setFocusDay(false);
  }, [open, focusDay, cursor]);

  const change = (value) => {
    emitted.current = value;
    onChangeISO(value);
  };
  const close = () => { returnFocus.current = true; setAnchor(null); };
  const choose = (value) => {
    if (value && !inRange(value)) return;
    setText(value ? pretty(value) : '');
    setBlurred(false);
    change(value);
    close();
  };
  const move = (value, focus = true) => {
    setCursor(/^\d{5}/.test(value) ? upper : clamp(value));
    if (focus) setFocusDay(true);
  };
  const showCalendar = (element) => {
    if (!validBounds) return;
    setCursor(clamp(parsed || selected || today));
    setAnchor(element);
  };
  const shiftMonth = (amount, focus = true) => move(moveMonth(cursor, amount), focus);
  const canPrevious = `${cursor.slice(0, 7)}-01` > `${lower.slice(0, 7)}-01`;
  const canNext = cursor.slice(0, 7) < upper.slice(0, 7);
  const days = useMemo(() => {
    const first = dateAt(year, month, 1);
    const offset = (first.getUTCDay() + 6) % 7;
    return Array.from({ length: 42 }, (_, index) => iso(new Date(first.getTime() + (index - offset) * DAY)));
  }, [year, month]);
  const dayKeys = (event, value) => {
    const date = fromISO(value);
    const weekday = (date.getUTCDay() + 6) % 7;
    const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -weekday, End: 6 - weekday };
    if (Object.hasOwn(offsets, event.key)) {
      event.preventDefault();
      move(iso(new Date(date.getTime() + offsets[event.key] * DAY)));
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      move(moveMonth(value, (event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1)));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); choose(value);
    } else if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); close();
    }
  };
  const applyYear = () => {
    const next = Number(yearText);
    if (/^\d{1,4}$/.test(yearText) && next >= 1 && next <= 9999) {
      const current = fromISO(cursor);
      const last = dateAt(next, month + 1, 0).getUTCDate();
      const bounded = clamp(iso(dateAt(next, month, Math.min(current.getUTCDate(), last))));
      setYearText(String(fromISO(bounded).getUTCFullYear()));
      move(bounded, false);
    } else setYearText(String(year));
  };

  return <>
    <TextField size="small" type="text" label={label} value={text} inputRef={inputRef}
      placeholder={translate('assistant.dashboardDateFieldDDMMYYYY')} error={invalid && (blurred || text.trim().length >= 10)}
      helperText={invalid && (blurred || text.trim().length >= 10) ? (parsed ? translate('assistant.dashboardDateFieldDateIsOutsideTheAvailablePeriod') : translate('assistant.dashboardDateFieldCheckTheDateDDMMYYYY')) : undefined}
      onChange={(event) => {
        const next = event.target.value;
        setText(next); setBlurred(false);
        const valid = parseDashboardDate(next);
        change(!next.trim() ? '' : valid && inRange(valid) ? valid : next);
      }}
      onBlur={() => { setBlurred(true); if (parsed && inRange(parsed)) setText(pretty(parsed)); }}
      onKeyDown={(event) => {
        if (event.altKey && event.key === 'ArrowDown') { event.preventDefault(); showCalendar(inputRef.current); }
      }}
      inputProps={{ 'aria-label': label, inputMode: 'numeric', maxLength: 10, autoComplete: 'off' }}
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title={translate('assistant.dashboardDateFieldOpenCalendar')}><IconButton type="button" size="small"
        aria-label={translate('assistant.dashboardDateFieldChooseDate', { value1: label })} aria-haspopup="dialog" aria-expanded={open} disabled={!validBounds}
        onClick={(event) => showCalendar(event.currentTarget)} sx={{ color: open ? t.link : t.inkMuted, borderRadius: 1.5 }}>
        <CalendarMonthRoundedIcon fontSize="small" />
      </IconButton></Tooltip></InputAdornment> }}
      sx={{ width: 198, maxWidth: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: t.surface }, '& .MuiInputBase-input': { fontVariantNumeric: 'tabular-nums' } }} />
    <Popover open={open} anchorEl={anchor} onClose={close} disableRestoreFocus
      transitionDuration={reduceMotion ? 0 : 120}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      TransitionProps={{ onEntered: () => focusCalendarDay(gridRef.current, paperRef.current, cursor), onExited: () => {
        if (returnFocus.current) { returnFocus.current = false; inputRef.current?.focus({ preventScroll: true }); }
      } }}
      PaperProps={{ ref: paperRef, role: 'dialog', 'aria-labelledby': titleId, sx: { p: 2.5, mt: 1, width: 328, maxWidth: 'calc(100vw - 24px)', borderRadius: 3, border: `1px solid ${t.borderField}`, bgcolor: t.surface, color: t.ink, backgroundImage: 'none', boxShadow: '0 16px 40px rgba(0,0,0,0.2)' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography id={titleId} variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>{label}</Typography>
        <IconButton type="button" size="small" aria-label={translate('assistant.dashboardDateFieldCloseCalendar')} onClick={close} sx={{ color: t.inkMuted }}><CloseRoundedIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
        <IconButton type="button" size="small" aria-label={translate('assistant.dashboardDateFieldPreviousMonth')} disabled={!canPrevious} onClick={() => shiftMonth(-1, false)}><ChevronLeftRoundedIcon fontSize="small" /></IconButton>
        <Select size="small" value={month} onChange={(event) => shiftMonth(Number(event.target.value) - month, false)}
          inputProps={{ 'aria-label': translate('assistant.dashboardDateFieldMonth') }} MenuProps={{ PaperProps: { sx: { borderRadius: 2.5, bgcolor: t.surface, color: t.ink } } }}
          sx={{ flexGrow: 1, fontSize: '0.85rem', borderRadius: 2, '& .MuiSelect-select': { pl: 1.25, py: 1 } }}>
          {MONTHS().map((name, index) => <MenuItem key={name} value={index} disabled={(year === Number(lower.slice(0, 4)) && index < Number(lower.slice(5, 7)) - 1) || (year === Number(upper.slice(0, 4)) && index > Number(upper.slice(5, 7)) - 1)}>{name}</MenuItem>)}
        </Select>
        <TextField size="small" value={yearText} onChange={(event) => setYearText(event.target.value.replace(/\D/g, '').slice(0, 4))}
          onBlur={applyYear} onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); applyYear(); setFocusDay(true); }
            else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); shiftMonth(event.key === 'ArrowUp' ? 12 : -12, false); }
          }}
          inputProps={{ 'aria-label': translate('assistant.dashboardDateFieldYear'), inputMode: 'numeric', maxLength: 4 }} sx={{ width: 74, '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& input': { px: 1.25, py: 1, fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' } }} />
        <IconButton type="button" size="small" aria-label={translate('assistant.dashboardDateFieldNextMonth')} disabled={!canNext} onClick={() => shiftMonth(1, false)}><ChevronRightRoundedIcon fontSize="small" /></IconButton>
      </Box>
      <Typography component="span" aria-live="polite" sx={{ position: 'absolute', top: 0, left: 0, width: '1px', height: '1px', overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{MONTHS()[month]} {year}</Typography>
      <Box ref={gridRef} role="grid" aria-label={`${MONTHS()[month]} ${year}`}>
        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
          {WEEKDAYS().map((day, index) => <Typography key={day} role="columnheader" aria-label={day} variant="caption" sx={{ textAlign: 'center', color: t.inkMuted, py: 0.75, fontWeight: 600 }}>{SHORT_WEEKDAYS()[index]}</Typography>)}
        </Box>
        {Array.from({ length: 6 }, (_, row) => <Box key={row} role="row" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, mb: 0.25 }}>
          {days.slice(row * 7, row * 7 + 7).map((value) => {
            const currentMonth = value.slice(0, 7) === cursor.slice(0, 7);
            const checked = value === selected;
            const bounded = isDashboardDateISO(value);
            return <Button key={value} type="button" role="gridcell" data-date={value} tabIndex={value === cursor ? 0 : -1}
              aria-label={bounded ? labelDate(value) : translate('assistant.dashboardDateFieldUnavailableDate')} aria-selected={checked} aria-current={value === today ? 'date' : undefined}
              disabled={!bounded || !inRange(value)} onClick={() => choose(value)} onKeyDown={(event) => dayKeys(event, value)}
              sx={{ minWidth: 0, width: '100%', height: 36, p: 0, borderRadius: 2, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums',
                color: checked ? t.onAccent : currentMonth ? t.ink : t.inkMuted,
                bgcolor: checked ? t.accent : 'transparent', border: '1px solid', borderColor: value === today && !checked ? t.link : 'transparent',
                '&:hover': { bgcolor: checked ? t.accentHover : t.raised }, '&.Mui-focusVisible': { outline: `2px solid ${t.focus}`, outlineOffset: 1 }, '&.Mui-disabled': { color: t.inkDim } }}>
              {Number(value.slice(8, 10))}
            </Button>;
          })}
        </Box>)}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2, pt: 1.5, borderTop: `1px solid ${t.border}` }}>
        <Button type="button" size="small" onClick={() => choose(today)} disabled={!inRange(today)} sx={{ px: 1.25, borderRadius: 2 }}>{translate('assistant.conversationHistoryToday')}</Button>
        <Button type="button" size="small" color="inherit" onClick={() => choose('')} sx={{ px: 1.25, borderRadius: 2, color: t.inkMuted }}>{translate('assistant.dashboardDateFieldClear')}</Button>
      </Box>
    </Popover>
  </>;
}
